import type { FormFieldDef, FieldSurface } from '@hcc/types';
import { FIELD_SURFACES, DESK_SURFACES } from '@hcc/types';

interface VisibilityConfigProps {
  draft: FormFieldDef;
  patch: (p: Partial<FormFieldDef>) => void;
}

const SURFACE_LABELS: Record<FieldSurface, string> = {
  self: 'Inductee (self)',
  onboarding: 'HR / onboarding',
  ppe_desk: 'PPE desk (sizes)',
  all_desks: 'All desks (every station)',
};

/**
 * The data-classification block: special-category (GDPR Article 9) toggle
 * and the surface checkboxes controlling where the answer is visible.
 */
export function VisibilityConfig({ draft, patch }: VisibilityConfigProps) {
  const surfaces = draft.classification ?? ['self', 'onboarding'];
  function toggleSurface(s: FieldSurface) {
    const next = surfaces.includes(s) ? surfaces.filter((x) => x !== s) : [...surfaces, s];
    patch({ classification: next });
  }

  function surfaceDisabled(s: FieldSurface): boolean {
    // Special-category data may never cross to a desk; "All desks" already
    // covers the PPE desk, so its checkbox is redundant once that's on.
    if (draft.special_category && DESK_SURFACES.includes(s)) return true;
    // A composite address holds a structured value the desk view can't flatten.
    if (draft.type === 'address' && DESK_SURFACES.includes(s)) return true;
    if (s === 'ppe_desk' && surfaces.includes('all_desks')) return true;
    return false;
  }

  function toggleSpecial(on: boolean) {
    // Special-category data is forced to onboarding/self and never a desk.
    patch(
      on
        ? { special_category: true, classification: ['self', 'onboarding'] }
        : { special_category: false },
    );
  }

  return (
    <div className="rounded-token-sm border border-line/10 bg-surface-2 p-3">
      <label className="flex items-center gap-2 text-sm font-medium text-fg">
        <input
          type="checkbox"
          checked={!!draft.special_category}
          onChange={(e) => toggleSpecial(e.target.checked)}
        />
        Special-category data (GDPR Article 9)
      </label>
      <p className="mt-1 text-xs text-fg-dim">
        Ethnicity, religion, health, sexual orientation, gender. Forced to HR/inductee only
        and gated behind a consent step.
      </p>
      <div className="mt-3 text-xs font-medium text-fg-muted">Visible to</div>
      <div className="mt-1.5 flex flex-wrap gap-3">
        {FIELD_SURFACES.map((s) => (
          <label key={s} className="flex items-center gap-1.5 text-sm text-fg">
            <input
              type="checkbox"
              checked={surfaces.includes(s)}
              disabled={surfaceDisabled(s)}
              onChange={() => toggleSurface(s)}
            />
            {SURFACE_LABELS[s]}
          </label>
        ))}
      </div>
    </div>
  );
}
